<script setup>
// NOTICE: KAPTION-INTEGRATION
// Integrations → Rumo MCP: manage this workspace's MCP connections
// (OAuth-minted, purpose: 'mcp' access tokens). Connecting happens from the
// AI client via the MCP endpoint; this page lists and revokes.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import kaptionAccessTokens from 'dashboard/api/kaptionAccessTokens';
import kaptionMcpGrant from 'dashboard/api/kaptionMcpGrant';

const { t } = useI18n();

const loading = ref(true);
const connections = ref([]);
const mcpUrl = ref('');
const confirmingId = ref(null);

const activeConnections = computed(() =>
  connections.value.filter(c => c.status === 'active')
);

const load = async () => {
  loading.value = true;
  try {
    const { data } = await kaptionAccessTokens.getAll({ purpose: 'mcp' });
    connections.value = data.payload || data;
  } catch (e) {
    useAlert(t('KAPTION_MCP_INTEGRATION.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  load();
  try {
    const { data } = await kaptionMcpGrant.getScopes();
    mcpUrl.value = data.mcp_url;
  } catch (e) {
    // profile endpoint may 403 (2FA not set up) — endpoint hint is optional
  }
});

const copyUrl = async () => {
  await copyTextToClipboard(mcpUrl.value);
  useAlert(t('COMPONENTS.CODE.COPY_SUCCESSFUL'));
};

const revoke = async connection => {
  if (confirmingId.value !== connection.id) {
    confirmingId.value = connection.id;
    return;
  }
  try {
    await kaptionAccessTokens.revoke(connection.id);
    useAlert(t('KAPTION_MCP_INTEGRATION.REVOKED'));
    confirmingId.value = null;
    load();
  } catch (e) {
    useAlert(t('KAPTION_MCP_INTEGRATION.REVOKE_ERROR'));
  }
};

const formatDate = value =>
  value ? new Date(value).toLocaleDateString() : '—';
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="$t('KAPTION_MCP_INTEGRATION.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('KAPTION_MCP_INTEGRATION.NAME')"
        :description="$t('KAPTION_MCP_INTEGRATION.DESCRIPTION')"
        feature-name="rumo_mcp"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-5">
        <!-- Endpoint -->
        <div
          v-if="mcpUrl"
          class="bg-n-background rounded-xl border border-n-weak p-5"
        >
          <h5 class="text-sm font-semibold text-n-slate-12">
            {{ $t('KAPTION_MCP_INTEGRATION.ENDPOINT_TITLE') }}
          </h5>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ $t('KAPTION_MCP_INTEGRATION.ENDPOINT_DESC') }}
          </p>
          <div class="flex items-center gap-2 mt-3">
            <code
              class="flex-1 h-10 flex items-center px-3 text-sm rounded-lg bg-n-alpha-black2 border border-n-weak text-n-slate-12 overflow-x-auto whitespace-nowrap"
            >
              {{ mcpUrl }}
            </code>
            <Button
              type="button"
              faded
              icon="i-lucide-copy"
              class="flex-shrink-0"
              @click="copyUrl"
            />
          </div>
        </div>

        <!-- Connections -->
        <div class="bg-n-background rounded-xl border border-n-weak">
          <div class="flex items-center justify-between px-5 py-4">
            <h5 class="text-sm font-semibold text-n-slate-12">
              {{ $t('KAPTION_MCP_INTEGRATION.CONNECTIONS_TITLE') }}
            </h5>
            <span class="text-xs text-n-slate-10">
              {{
                $t('KAPTION_MCP_INTEGRATION.ACTIVE_COUNT', {
                  count: activeConnections.length,
                })
              }}
            </span>
          </div>

          <div
            v-if="!connections.length"
            class="px-5 pb-5 text-sm text-n-slate-11"
          >
            {{ $t('KAPTION_MCP_INTEGRATION.EMPTY') }}
          </div>

          <div v-else class="border-t border-n-weak divide-y divide-n-weak">
            <div
              v-for="connection in connections"
              :key="connection.id"
              class="flex items-center gap-4 px-5 py-3"
            >
              <span
                class="flex items-center justify-center size-8 rounded-full bg-n-alpha-black2 flex-shrink-0"
              >
                <Icon icon="i-lucide-bot" class="size-4 text-n-slate-11" />
              </span>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <span class="text-sm font-medium text-n-slate-12 truncate">
                    {{ connection.name }}
                  </span>
                  <span
                    class="px-1.5 py-0.5 text-xs rounded-full flex-shrink-0"
                    :class="
                      connection.status === 'active'
                        ? 'bg-n-teal-3 text-n-teal-11'
                        : 'bg-n-alpha-black2 text-n-slate-10'
                    "
                  >
                    {{ connection.status }}
                  </span>
                </div>
                <p class="text-xs text-n-slate-10 truncate">
                  {{ connection.owner?.name }} ·
                  {{
                    $t('KAPTION_MCP_INTEGRATION.PERMISSIONS_COUNT', {
                      count: (connection.scopes || []).length,
                    })
                  }}
                  · {{ (connection.scopes || []).join(', ') }}
                </p>
              </div>
              <div
                class="text-xs text-n-slate-10 text-right flex-shrink-0 hidden sm:block"
              >
                <div>
                  {{ $t('KAPTION_MCP_INTEGRATION.CONNECTED_ON') }}
                  {{ formatDate(connection.created_at) }}
                </div>
                <div>
                  {{ $t('KAPTION_MCP_INTEGRATION.LAST_USED') }}
                  {{ formatDate(connection.last_used_at) }}
                </div>
              </div>
              <Button
                v-if="connection.status === 'active'"
                type="button"
                faded
                ruby
                sm
                :label="
                  confirmingId === connection.id
                    ? $t('KAPTION_MCP_INTEGRATION.REVOKE_CONFIRM')
                    : $t('KAPTION_MCP_INTEGRATION.REVOKE')
                "
                class="flex-shrink-0"
                @click="revoke(connection)"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
